import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Folder, Tag } from 'lucide-react';
import { PostCard } from '../components/PostCard';
import { api } from '../api';
import { FolderCount } from '../types';
import { buildFolderTree, countItems, FolderNode } from '../lib/tree';

/** 文库：目录树 + 标签 + 搜索，全站文章的统一查询入口 */
export function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputQ, setInputQ] = useState(searchParams.get('q') || '');

  const tag = searchParams.get('tag') || undefined;
  const q = searchParams.get('q') || undefined;
  const folder = searchParams.get('folder') || undefined;

  const { data: posts, isLoading } = useQuery({
    queryKey: ['posts', { tag, q, folder }],
    queryFn: () => api.listPosts({ tag, q, folder }),
  });

  const { data: folders } = useQuery({
    queryKey: ['folders'],
    queryFn: () => api.listFolders(),
  });

  const { data: tags } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
  });

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilter('q', inputQ || undefined);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
    >
      <header className="mb-10">
        <h1 className="mb-3 font-serif text-4xl font-bold tracking-tight text-stone-900 dark:text-star">
          文库
        </h1>
        <p className="text-stone-600 dark:text-star-dim">按目录、标签或关键词检索全部文章。</p>
      </header>

      <div className="grid gap-12 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-8">
            {folders && folders.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-star-dim">
                  <Folder size={14} className="text-indigo-500 dark:text-nebula-glow" />
                  目录
                </h2>
                <FolderTree folders={folders} active={folder} onSelect={(path) => setFilter('folder', path)} />
              </section>
            )}

            {tags && tags.length > 0 && (
              <section>
                <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-star-dim">
                  <Tag size={14} className="text-indigo-500 dark:text-nebula-glow" />
                  标签
                </h2>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <button
                      key={t.name}
                      onClick={() => setFilter('tag', tag === t.name ? undefined : t.name)}
                      className={`tag-pill border ${
                        tag === t.name
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-nebula-glow'
                          : 'border-stone-200 text-stone-600 hover:border-indigo-500 hover:text-indigo-600 dark:border-indigo-500/20 dark:text-star-dim dark:hover:text-nebula-glow'
                      }`}
                    >
                      {t.name}
                      <span className="text-xs text-stone-400">{t.count}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        </aside>

        <div>
          <form onSubmit={handleSearch} className="relative mb-8 w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input
              type="text"
              value={inputQ}
              onChange={(e) => setInputQ(e.target.value)}
              placeholder="搜索文章..."
              className="input-glow w-full rounded-full border border-stone-200 bg-white py-2.5 pl-10 pr-4 text-sm text-stone-900 outline-none focus:border-indigo-500 dark:border-indigo-500/20 dark:bg-space-800 dark:text-star dark:placeholder:text-slate-500"
            />
          </form>

          {(tag || folder || q) && (
            <div className="mb-6 flex flex-wrap items-center gap-4 text-sm text-stone-600 dark:text-star-dim">
              {folder && <FilterChip label={`目录：${folder}`} onClear={() => setFilter('folder')} />}
              {tag && <FilterChip label={`标签：${tag}`} onClear={() => setFilter('tag')} />}
              {q && <FilterChip label={`搜索：${q}`} onClear={() => { setInputQ(''); setFilter('q'); }} />}
            </div>
          )}

          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 animate-pulse rounded-2xl bg-stone-200 dark:bg-space-800"></div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {posts?.map((post) => (
                <PostCard key={post.id} post={post} featured={!!post.cover_url} />
              ))}
            </div>
          )}

          {!isLoading && posts?.length === 0 && (
            <div className="py-20 text-center text-stone-500 dark:text-star-dim">没有找到文章</div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="flex items-center gap-2">
      <span className="tag-pill bg-indigo-100 font-medium text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
        {label}
      </span>
      <button onClick={onClear} className="text-stone-400 hover:text-stone-600 dark:hover:text-star">
        清除
      </button>
    </span>
  );
}

function FolderTree({
  folders,
  active,
  onSelect,
}: {
  folders: FolderCount[];
  active?: string;
  onSelect: (path?: string) => void;
}) {
  const tree = useMemo(() => buildFolderTree(folders, (f) => f.name), [folders]);

  return (
    <ul className="space-y-1 text-sm">
      <li>
        <button
          onClick={() => onSelect(undefined)}
          className={`w-full rounded-lg px-2 py-1.5 text-left transition-colors ${
            !active
              ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-nebula-glow'
              : 'text-stone-600 hover:bg-stone-100 dark:text-star-dim dark:hover:bg-space-800'
          }`}
        >
          全部文章
        </button>
      </li>
      {tree.children.map((node) => (
        <FolderTreeNode key={node.path} node={node} depth={0} active={active} onSelect={onSelect} />
      ))}
    </ul>
  );
}

function FolderTreeNode({
  node,
  depth,
  active,
  onSelect,
}: {
  node: FolderNode<FolderCount>;
  depth: number;
  active?: string;
  onSelect: (path?: string) => void;
}) {
  const isActive = active === node.path;

  return (
    <li>
      <button
        onClick={() => onSelect(node.path)}
        className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left transition-colors ${
          isActive
            ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-nebula-glow'
            : 'text-stone-600 hover:bg-stone-100 dark:text-star-dim dark:hover:bg-space-800'
        }`}
        style={{ paddingLeft: `${depth * 1 + 0.5}rem` }}
      >
        <span className="truncate">{node.name}</span>
        <span className="text-xs text-stone-400">{countItems(node)}</span>
      </button>
      {node.children.length > 0 && (
        <ul className="mt-1 space-y-1">
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              active={active}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
